import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import _ from "lodash";
import styled, { css, FlattenSimpleInterpolation } from "styled-components";

import ImportInput from "./input";
import ImportTextarea from "./textarea";

import { createId, getElementClassPath, sleep } from "services/common-service";

import useComponentSize from "hooks/useComponentSize";
import useEventListener from "hooks/useEventListener";
import useOnClickOutside from "hooks/useOnClickOutside";

import { EEvents } from "types/enums";

export const Input = (props: any) => <ImportInput {...props} />;
export const Textarea = (props: any) => <ImportTextarea {...props} />;

export type TExtra =
    string
    | FlattenSimpleInterpolation
    | ((theme: any) => string)
    | ((theme: any) => FlattenSimpleInterpolation);

export const Frame = styled.div<{ extra?: TExtra; ref?: any }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  transition: 0.2s;

  ${({ extra }) => extra}
`;

export const Row = styled(Frame)<{ extra?: TExtra; ref?: any }>`
  flex-direction: row;

  ${({ extra }) => extra}
`;

export const RowWrapper = styled(Frame)<{ extra?: TExtra }>`
  width: 100%;
  flex-direction: row;
  justify-content: space-between;

  ${({ extra }) => extra}
`;

export const H1 = styled(Frame)<{ extra?: TExtra }>`
  font-weight: bold;
  font-size: 24px;
  line-height: 24px;
  color: ${({ theme }) => theme.text.primary};
  flex-direction: row;
  justify-content: flex-start;

  span {
    margin: 0 5px;
    color: ${({ theme }) => theme.blue};
  }

  ${({ extra }) => extra}
`;

export const H2 = styled(Frame)<{ extra?: TExtra }>`
  color: ${({ theme }) => theme.text.primary};
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  align-items: flex-start;

  ${({ extra }) => extra}
`;

export const H3 = styled(Frame)<{ extra?: TExtra }>`
  color: ${({ theme }) => theme.text.primary};
  font-weight: 600;
  font-size: 14px;
  line-height: 19px;

  ${({ extra }) => extra}
`;

export const Container = styled(Frame)<{ extra?: TExtra }>`
  width: 100%;
  max-width: 1400px;
  /* min-width: 700px; */

  min-height: min-content;

  padding: 0 5px;
  box-sizing: border-box;

  @media (max-width: 991px) {
    max-width: calc(100vw - 10px);
    box-sizing: border-box;
    //max-width: 99vw;
  }

  ${({ extra }) => extra}
`;

/* CHECKBOX */

export const Checkbox = styled.input.attrs((props) => {
    console.log('Checkbox: props = ', props);
    return {
        ...props,
        type: `checkbox`
    };
})`
  width: 20px;
  height: 20px;
  background: ${({ theme }) => theme.background.secondary};
  border: 1px solid #c0c0c0;
  border-radius: 4px;
  cursor: pointer;
`;

/* RADIO */

export const Radio = styled.input.attrs((props) => {
    return {
        ...props,
        type: `radio`
    };
})`
  width: 20px;
  height: 20px;
  background: ${({ theme }) => theme.background.secondary};
  border: 1px solid #c0c0c0;
  border-radius: 9999px;
  cursor: pointer;
`;

/* SWITCH */

const SwitchWrapper = styled(Frame)<{ checked?: boolean; disabled?: boolean }>`
  position: relative;
  width: 39px !important;
  max-width: 39px;
  height: 24px;
  border-radius: 9999px;
  background: ${({ theme, checked = false }) => (checked ? theme.green : `#C1C9D2`)};
  cursor: ${({ disabled = false }) => (disabled ? `default` : `pointer`)};

  &:after {
    content: "";
    transition: 0.2s;
    width: 18px;
    height: 18px;
    background: white;
    border-radius: 9999px;
    position: absolute;
      /* ${({ checked = false }) => (checked ? `right` : `left`)}: 4px; */
    left: ${({ checked = false }) => (checked ? `calc(100% - 3px - 18px)` : `3px`)};
  }

  ${({ extra }) => extra}
`;

export const Switch = (props: { name?: string; extra?: TExtra; value: boolean; onChange: Function }) => {
    const {
        name = `checkbox`, extra = ``, value = false, onChange = () => {
        }
    } = props;
    return <SwitchWrapper onClick={() => onChange({ target: { value: !value } })} checked={value} extra={extra} />;
};

/* FORM */

export const Form = styled.form<{ name: string; extra?: TExtra }>`
  width: 100%;
  align-items: flex-start;

  ${({ extra }) => extra}
`;

/* SPINNER */

export const Spinner = styled(Frame)`
  width: 18px;
  height: 18px;
  background: url("${require("../../assets/images/spinner.svg").default}") no-repeat center center / contain;

  ${({ extra }) => extra}
`;

/* BUTTON */

const ASYNC_STATUSES = {
    REGULAR: `REGULAR`,
    PENDING: `PENDING`,
    SUCCESS: `SUCCESS`,
    ERROR: `ERROR`
};

export const Button = (props: any) => {
    const {
        extendedIndicators = false,
        onClick,
        disabled = false,
        successLabel,
        errorLabel,
        extra = ``,
        leftIcon,
        type = `button`
    } = props;
    const [status, setStatus] = useState(ASYNC_STATUSES.REGULAR);

    const newExtra =
        {
            [ASYNC_STATUSES.PENDING]: (props: any) => `background: ${props.theme.grey} !important;`,
            [ASYNC_STATUSES.SUCCESS]: (props: any) => `background: ${props.theme.green} !important;`,
            [ASYNC_STATUSES.ERROR]: (props: any) => `background: ${props.theme.red} !important;`
        }?.[status] ?? ``;

    const newOnClick = async (e: any) => {
        if (!onClick) {
            return;
        }
        try {
            setStatus(`pending`);
            await onClick(e);
            setStatus(extendedIndicators ? ASYNC_STATUSES.SUCCESS : ASYNC_STATUSES.REGULAR);
        } catch (error) {
            setStatus(extendedIndicators ? ASYNC_STATUSES.ERROR : ASYNC_STATUSES.REGULAR);
        } finally {
            if (extendedIndicators) {
                await sleep(1000);
                setStatus(`retular`);
            }
        }
    };

    const newDisabled = disabled === true || [ASYNC_STATUSES.PENDING, ASYNC_STATUSES.SUCCESS, ASYNC_STATUSES.ERROR].includes(status);

    return (
        <ButtonWrapper {...props} extra={extra} importantExtra={newExtra} onClick={newOnClick} disabled={newDisabled}
                       leftIcon={leftIcon} type={type}>
            {{
                pending: <Spinner />,
                success: successLabel ?? `Success`,
                error: errorLabel ?? `Error`
            }[status] ?? props?.children}
        </ButtonWrapper>
    );
};

type TButtonWrapperProps = {
    variant?: string;
    disabled?: boolean;
    leftIcon?: string;
    leftIconStyles?: string;
    extra?: TExtra;
    importantExtra?: TExtra;
};
export const ButtonWrapper = styled.button<TButtonWrapperProps>`
  color: white;
  font-family: "Noto Sans", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  padding: 18px 24px;
  min-width: 164px;
  border-radius: 9999px;
  border: 0px;
  box-sizing: border-box;
  transition: 0.2s;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  @media(max-width: 800px){
    min-width: 124px;
    padding: 12px 10px;
    font-size: 14px;
  }

  ${({ variant, disabled }) =>
          variant !== `outlined` &&
          !disabled &&
          css`
            box-shadow: 0px 4px 12px rgba(13, 90, 220, 0.25);
          `}
  &:hover {
    opacity: 0.8;
  }

  ${({ leftIcon }) =>
          leftIcon &&
          css`
            &:before {
              content: "";
              width: 10px;
              height: 10px;
              background: url("${require(`../../assets/icons/${leftIcon}.svg`).default}") no-repeat center center / contain;
            }
          `}

  ${({ leftIcon, leftIconStyles = `` }) =>
          leftIcon &&
          css`
            &:before {
              content: "";
              width: 20px;
              height: 20px;
              margin-right: 8px;
              background: url("${require(`../../assets/icons/${leftIcon}.svg`).default}") no-repeat center center / contain;
              ${leftIconStyles}
            }
          `}

  ${(props) =>
          (() => {
            const { background = props.theme.blue, theme = {}, variant = `primary` } = props as any;
            return (
                    (
                            {
                              contained: css`
                                background: ${_.get(theme, background) ?? background};
                              `,
                              outlined: css`
                                background: transparent;
                                border: 1px solid ${_.get(theme, background) ?? background};
                                color: ${_.get(theme, background) ?? background};
                              `,
                              plain: css`
                                background: transparent;
                                color: ${_.get(theme, background) ?? background};

                                &:hover {
                                  background: ${({ theme }) => theme.background.secondary};
                                }
                              `
                            } as any
                    )?.[variant ?? `primary`] ??
                    css`
                      background: ${_.get(theme, background) ?? background};
                    `
            );
          })()}

  ${({ disabled }) =>
          disabled &&
          css`
            cursor: default;
            opacity: 0.5;

            &:hover {
              opacity: 0.5;
            }
          `}

  ${({ extra }) => extra}
  ${({ importantExtra }) => importantExtra}
`;

/* DROPDOWN */

const DropdownStyles = {
    Wrapper: styled(Frame)<{ extra?: TExtra }>`
      position: relative;

      ${({ extra }) => extra}
    `,
    Toggle: styled(Frame).attrs((props) => {
        return {
            ...props,
            className: `dropdown-toggle`
        };
    })``,
    Menu: styled(Frame).attrs((props) => {
        return {
            ...props,
            className: `dropdown-menu`
        };
    })<{ direction?: string; toggleSize?: any; visible?: boolean; extra?: TExtra }>`
      flex: 1;
      overflow: auto;
      width: inherit;
      max-width: calc(100vw - 120px) !important;
      /* max-height: 200px; */

      position: absolute;
      ${({ direction, toggleSize }) =>
              ({
                down: css`
                  top: ${toggleSize.height}px;
                `,
                up: css`
                  bottom: ${toggleSize.height}px;
                `
              }?.[direction ?? `down`])}
      right: 0;
      z-index: 2;
      visibility: ${({ visible }) => (visible ? `visible` : `hidden`)};
      opacity: ${({ visible }) => (visible ? 1 : 0)};
      transform: translate(0, ${({ visible, direction = `down` }) => (visible ? {
        down: 5,
        up: -5
      }?.[direction] : -15)}px);

      padding: 10px;
      border-radius: 8px;
      background: ${({ theme }) => theme.background.primary};
      border: 1px solid ${({ theme }) => theme.background.secondary};
      box-sizing: border-box;
      filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.05));

      ${({ extra }) => extra}
    `,
    ScrollWrapper: styled.div<{ extra?: TExtra }>`
      width: 100%;
      display: flex;
      flex-direction: column;
      //min-height: min-content;
      /* max-height: 200px; */

      ${({ extra }) => extra}
    `
};

type TDropdownProps = {
    toggle: Function;
    menu: Function;
    wrapperStyles?: string;
    scrollWrapperStyles?: TExtra;
    id?: string;
    closeOnChildrenClick?: boolean;
    closeOnToggleClick?: boolean;
    callable?: boolean;
    toggleProps?: Object;
    menuProps?: Object;
    isOpen?: boolean;
    onOpenChange?: (opened: boolean) => void;
};

export const Dropdown = (props: TDropdownProps) => {
    const {
        toggle: ToggleComponent,
        menu: MenuComponent,
        wrapperStyles = ``,
        scrollWrapperStyles = ``,
        id,
        closeOnChildrenClick = true,
        closeOnToggleClick = true,
        callable = true,
        toggleProps = {},
        menuProps = {},
        isOpen: externalIsOpen,
        onOpenChange
    } = props;

    const [internalOpened, setInternalOpened] = useState(false);

    // Use external control if provided, otherwise use internal state
    const isControlled = externalIsOpen !== undefined;
    const opened = isControlled ? externalIsOpen : internalOpened;

    const setOpened = (value: boolean) => {
        if (!isControlled) {
            setInternalOpened(value);
        }
        onOpenChange?.(value);
    };
    const menuRef = useRef();
    const toggleRef = useRef();
    const uniqueId = useRef(createId());
    const [direction, setDirection] = useState(`down`);

    useOnClickOutside(menuRef, (e: any) => {
        // Используем composedPath() для совместимости с современными браузерами (e.path устарел)
        const path = e?.composedPath?.() || e?.path || [];
        const isCurrentDropdown = path.map?.((i: any) => i.className?.includes?.(uniqueId.current)).filter((i: any) => i).length === 0;
        if (isCurrentDropdown) {
            setOpened(false);
        }
    });
    useEventListener(EEvents.closeDropdown, (d: any) => {
        if (!!id && id === d?.detail) {
            setOpened(false);
        }
    });
    useEffect(() => {
        setDirection(
            window?.outerHeight - ((menuRef as any).current.getBoundingClientRect().y + window.scrollY) - (toggleRef as any)?.current?.clientHeight >
            (menuRef as any)?.current?.clientHeight
                ? `down`
                : `up`
        );
    }, [menuRef, toggleRef]);

    const toggleSize = useComponentSize(toggleRef);

    const handleMenuItemClick = (e: any) => {
        if (
            getElementClassPath(e?.target)
                .filter((i, j) => j > 1)
                .find((i) => i?.includes(`dropdown-menu`)) &&
            closeOnChildrenClick
        ) {
            setOpened(false);
        }
    };

    const handleToggleClick = (event: any) => {
        if (!callable || (!closeOnToggleClick && opened)) return;
        setOpened(!opened);
        setDirection(
            window?.outerHeight - event?.screenY - (toggleRef as any)?.current?.clientHeight > (menuRef as any)?.current?.clientHeight
                ? `down`
                : `up`
        );
    };

    return (
        <DropdownStyles.Wrapper extra={wrapperStyles} className={uniqueId.current}>
            <DropdownStyles.Toggle {...toggleProps} ref={toggleRef} onClick={handleToggleClick}>
                <ToggleComponent {...{ opened, uniqueId, direction }} />
            </DropdownStyles.Toggle>
            <DropdownStyles.Menu
                {...menuProps}
                ref={menuRef}
                visible={opened}
                toggleSize={toggleSize}
                onClick={handleMenuItemClick}
                direction={direction}
            >
                <DropdownStyles.ScrollWrapper extra={scrollWrapperStyles}>
                    <MenuComponent {...{ opened, uniqueId, direction }} />
                </DropdownStyles.ScrollWrapper>
            </DropdownStyles.Menu>
        </DropdownStyles.Wrapper>
    );
};

// HoverDropdown - выпадающий список при наведении курсора
const HoverDropdownStyles = {
    Wrapper: styled(Frame)<{ extra?: TExtra }>`
      position: relative;
      ${({ extra }) => extra}
    `,
    Toggle: styled(Frame)`
      cursor: pointer;
    `,
    Menu: styled(Frame)<{ direction?: string; toggleSize?: any; visible?: boolean; extra?: TExtra }>`
      flex: 1;
      overflow: auto;
      width: inherit;
      min-width: 180px;
      max-width: calc(100vw - 120px) !important;

      position: absolute;
      ${({ direction, toggleSize }) =>
              ({
                down: css`
                  top: ${toggleSize?.height || 0}px;
                `,
                up: css`
                  bottom: ${toggleSize?.height || 0}px;
                `
              }?.[direction ?? `down`])}
      right: 0;
      z-index: 10;
      visibility: ${({ visible }) => (visible ? `visible` : `hidden`)};
      opacity: ${({ visible }) => (visible ? 1 : 0)};
      transform: translate(0, ${({ visible, direction = `down` }) => (visible ? {
        down: 5,
        up: -5
      }?.[direction] : -15)}px);

      padding: 10px;
      border-radius: 8px;
      background: ${({ theme }) => theme.background.primary};
      border: 1px solid ${({ theme }) => theme.background.secondary};
      box-sizing: border-box;
      filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.15));
      transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s;

      ${({ extra }) => extra}
    `,
    ScrollWrapper: styled.div<{ extra?: TExtra }>`
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 4px;

      ${({ extra }) => extra}
    `
};

type THoverDropdownProps = {
    toggle: Function;
    menu: Function;
    wrapperStyles?: string;
    scrollWrapperStyles?: TExtra;
    toggleProps?: Object;
    menuProps?: Object;
};

export const HoverDropdown = (props: THoverDropdownProps) => {
    const {
        toggle: ToggleComponent,
        menu: MenuComponent,
        wrapperStyles = ``,
        scrollWrapperStyles = ``,
        toggleProps = {},
        menuProps = {}
    } = props;

    const [opened, setOpened] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [direction, setDirection] = useState(`down`);
    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const toggleSize = useComponentSize(toggleRef);

    const handleMouseEnter = () => {
        // Очищаем таймер закрытия, если он был
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }

        // Определяем направление открытия меню
        if (toggleRef.current && menuRef.current) {
            const rect = toggleRef.current.getBoundingClientRect();
            const menuHeight = menuRef.current.clientHeight || 200;
            const spaceBelow = window.innerHeight - rect.bottom;
            setDirection(spaceBelow > menuHeight ? `down` : `up`);
        }
        setOpened(true);
    };

    const handleMouseLeave = () => {
        // Закрываем меню с небольшой задержкой для плавности
        closeTimeoutRef.current = setTimeout(() => {
            setOpened(false);
        }, 100);
    };

    const handleMenuItemClick = () => {
        // Закрываем меню после выбора пункта
        setOpened(false);
    };

    // Очистка таймера при размонтировании
    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
        };
    }, []);

    return (
        <HoverDropdownStyles.Wrapper
            extra={wrapperStyles}
            ref={wrapperRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <HoverDropdownStyles.Toggle {...toggleProps} ref={toggleRef}>
                <ToggleComponent {...{ opened, direction }} />
            </HoverDropdownStyles.Toggle>
            <HoverDropdownStyles.Menu
                {...menuProps}
                ref={menuRef}
                visible={opened}
                toggleSize={toggleSize}
                onClick={handleMenuItemClick}
                direction={direction}
            >
                <HoverDropdownStyles.ScrollWrapper extra={scrollWrapperStyles}>
                    <MenuComponent {...{ opened, direction }} />
                </HoverDropdownStyles.ScrollWrapper>
            </HoverDropdownStyles.Menu>
        </HoverDropdownStyles.Wrapper>
    );
};

const Div = styled.div<{ extra?: TExtra }>`
  ${({ extra }) => extra}
`;

/* BREADCRUMB */

const BreadcrumbWrapper = styled(RowWrapper)`
  flex-direction: row;
  justify-content: flex-start;
  padding: 20px 0;
  box-sizing: border-box;
`;

const BreadcrumbSeparator = styled(RowWrapper)`
  width: 15px;
  height: 15px;
    /* background: url("${require(`../../assets/icons/arrow-right.svg`).default}") no-repeat center center / contain; */
  margin: 0 8px;

  color: ${({ theme }) => theme.grey};
  justify-content: center;
`;

export const Breadcrumb = {
    Wrapper: (props: any) => {
        return (
            <BreadcrumbWrapper>
                {(Array.isArray(props.children) ? Array.from(props.children ?? []) : [props.children])?.map?.((child, index, self) => (
                    <Fragment key={index}>
                        {child}
                        {index < self.length - 1 && <BreadcrumbSeparator>•</BreadcrumbSeparator>}
                    </Fragment>
                ))}
            </BreadcrumbWrapper>
        );
    },
    Item: styled(Link)<{ selected?: boolean }>`
      color: ${({ theme }) => theme.grey};

      ${({ theme, selected }) =>
              selected &&
              css`
                font-weight: 400;
                font-size: 15px;
                line-height: 20px;
                color: ${theme.grey};
              `}
    `
};

type TScrollableProps = {
    children: any;
    outerExtra?: TExtra;
    innerExtra?: TExtra;
};

export const Scrollable = ({ children, outerExtra = ``, innerExtra = `` }: TScrollableProps) => {
    return (
        <Frame extra={`width: inherit; min-height: min-content; ${outerExtra}`}>
            <Frame
                extra={`width: inherit; flex: 1; overflow: auto; justify-content: flex-start; ${innerExtra}`}>{children}</Frame>
        </Frame>
    );
};

// GUEST PAGE COMMON ELEMENTS
export const Logo = styled.img.attrs((props) => {
    // return { ...props, src: require("assets/images/logo.svg").default };
    return { ...props, src: require(`assets/images/${props?.src ?? `logo_new_2.svg`}`) };
})<{ extra?: TExtra }>`
  height: 54px;

  ${({ extra }) => extra};
`;

export const AuthStyles = {
    Container: styled(Frame)`
      background: #ffffff;
      border: 1px solid ${({ theme }) => theme.halfblue};
      border-radius: 8px;
      padding: 40px 32px;
      box-sizing: content-box;
      width: 389px;

      p {
        font-weight: 700;
        font-size: 18px;
        line-height: 27px;
        text-align: center;
        letter-spacing: 0.01em;
        color: ${({ theme }) => theme.text.secondary};

        margin: -13px 0 20px 0;
      }

      @media (max-width: ${({ theme }) => theme.size.tablet}px) {
        width: 100%;
        border: 0px;
        border-radius: 0px;
        //min-height: 100vh;
        padding: 40px 24px;
        box-sizing: border-box;
      }

      ${({ extra }) => extra}
    `,
    LinksWrapper: styled(Frame)`
      margin-top: 16px;

      > * {
        padding: 8px 0;
        color: ${({ theme }) => theme.blue};
        text-align: center;

        &:hover {
          opacity: 0.5;
        }
      }
    `
};

export type TPhotoDropzoneProps = { src?: any; label?: string; icon?: string };

export const PhotoDropzone = styled(Frame)<TPhotoDropzoneProps>`
  width: 100%;
  padding: 50px;
  box-sizing: border-box;
  border-radius: 2px;

  ${({ src }) =>
          src &&
          css`
            background: url(${src}) no-repeat center center / cover;
          `}

  ${({ theme, src, label = `Загрузить фотографию (не более 1Мб, формат JPG)`, icon = `dropzone-image` }) =>
          !src &&
          css`
            background: rgba(201, 212, 219, 0.1);
            border: 1px dashed rgba(201, 212, 219, 0.5);

            &:before {
              content: "";
              width: 51px;
              height: 51px;
              background: url("${require(`../../assets/icons/${icon}.svg`)}") no-repeat center center / contain;
            }

            &:after {
              content: "${label}";
              color: ${theme.blue};
              margin-top: 30px;
            }
          `}
`;
